/**
 * showcaseStanding.ts — where ONE of your Pokémon stands among everything
 * you have scanned, for the record page (2026-09-20 owner ask: "showcase
 * data in Worth knowing: score, rank in its type, rank overall, and a big
 * call-out for any top-5, because a showcase day takes five entries").
 *
 * A PokéStop Showcase day names one type and takes five entries per
 * trainer, so your five best of that type are the ones that matter, and a
 * record page that says "Consider transferring" over your best Ghost is
 * wrong. The standing ranks this record inside each of its types and
 * overall, exactly as the Showcases page ranks them (showcaseCandidates),
 * and says whether it is a TOP-5 pick anywhere.
 *
 * Pure. Types come from showcaseTypeGroups, so the species table decides
 * (myPokemonTypes), as on the Showcases page.
 */
import type { MyPokemonRecord } from "./myPokemonStore";
import type { MyPokemonRecommendation } from "./myPokemonRecommendations";
import { compareCandidates, showcaseTypeGroups, type ShowcaseCandidate } from "./showcaseCandidates";
import { formatShowcaseScore, showcaseScoreFor, type ShowcaseScore } from "./showcaseScore";
import { ALL_TYPES, type PokeType } from "./types";

/** A showcase day takes this many entries per trainer. */
export const SHOWCASE_PICKS_PER_DAY = 5;

export interface ShowcaseTypeStanding {
  type: PokeType;
  /** 1-based rank among your scored Pokémon of this type. */
  rank: number;
  /** How many of your Pokémon of this type are scored. */
  of: number;
}

export interface ShowcaseStanding {
  score: ShowcaseScore;
  types: ShowcaseTypeStanding[];
  overall: { rank: number; of: number };
  /** Top-5 in at least one type or overall: a showcase pick. */
  pick: boolean;
  /** The types this record is a top-5 pick for (empty when only overall). */
  pickTypes: PokeType[];
  pickOverall: boolean;
}

/**
 * The record's standing, or null when it has no showcase score (no height
 * read) or is not among the records given.
 */
export function showcaseStandingFor(
  records: readonly MyPokemonRecord[],
  record: MyPokemonRecord,
): ShowcaseStanding | null {
  const score = showcaseScoreFor(record);
  if (!score) return null;
  const groups = showcaseTypeGroups(records);
  const types: ShowcaseTypeStanding[] = [];
  for (const g of groups) {
    const i = g.candidates.findIndex((c) => c.record.id === record.id);
    if (i < 0) continue;
    types.push({ type: g.type, rank: i + 1, of: g.candidates.length });
  }
  if (types.length === 0) return null;
  // Overall: every scored record once, best first (a dual type is one entry).
  const seen = new Set<string>();
  const all: ShowcaseCandidate[] = [];
  for (const g of groups) {
    for (const c of g.candidates) {
      if (seen.has(c.record.id)) continue;
      seen.add(c.record.id);
      all.push(c);
    }
  }
  all.sort(compareCandidates);
  const overallRank = all.findIndex((c) => c.record.id === record.id) + 1;
  const pickTypes = types.filter((t) => t.rank <= SHOWCASE_PICKS_PER_DAY).map((t) => t.type);
  const pickOverall = overallRank > 0 && overallRank <= SHOWCASE_PICKS_PER_DAY;
  return {
    score,
    types,
    overall: { rank: overallRank, of: all.length },
    pick: pickTypes.length > 0 || pickOverall,
    pickTypes,
    pickOverall,
  };
}

function typeLabel(t: PokeType): string {
  return ALL_TYPES.find((x) => x.key === t)?.label ?? t;
}

/** What the native HUD's showcase strip says: the best top-5 standing. */
export interface ShowcaseHudPick {
  /** 1-based rank inside the type (or overall). */
  rank: number;
  /** "Grass", or "Overall" when the record is a pick only across types. */
  label: string;
  /** The score as the Showcases page prints it ("~1,081" when the ceiling is a guess). */
  score: string;
}

/**
 * The one showcase claim worth a HUD strip (2026-09-22 owner ask: "if it
 * makes their top 5 for a category, show it"): the best type standing that
 * is a top-5, else the overall top-5, else null.
 */
export function showcaseHudPick(standing: ShowcaseStanding | null): ShowcaseHudPick | null {
  if (!standing || !standing.pick) return null;
  const score = formatShowcaseScore(standing.score);
  const best = standing.types
    .filter((t) => t.rank <= SHOWCASE_PICKS_PER_DAY)
    .sort((a, b) => a.rank - b.rank)[0];
  if (best) return { rank: best.rank, label: typeLabel(best.type), score };
  if (standing.pickOverall) return { rank: standing.overall.rank, label: "Overall", score };
  return null;
}

/**
 * The HUD bridge fields for a showcase pick (2026-10-26). The native HUDs
 * keep these sticky (absent leaves) and carry NO clock of their own: the
 * standing stays on the card until the next push says otherwise. So every
 * Pokémon push sends its own standing, and a Pokémon without one, or the
 * card's release, sends the EXPLICIT clear (rank 0, empty label and score),
 * never undefined. That is what takes a previous Pokémon's standing down
 * when a different one is on screen, on iOS and Android alike.
 */
export interface ShowcaseHudFields {
  showcaseRank: number;
  showcaseLabel: string;
  showcaseScore: string;
}

export function showcaseHudFields(pick: ShowcaseHudPick | null | undefined): ShowcaseHudFields {
  if (!pick || !(pick.rank > 0)) return { showcaseRank: 0, showcaseLabel: "", showcaseScore: "" };
  return { showcaseRank: pick.rank, showcaseLabel: pick.label, showcaseScore: pick.score };
}

/**
 * The Worth-knowing line for a showcase pick: the reason to keep it, in one
 * sentence, ahead of any trade-or-transfer call. Null when the record is
 * not a top-5 anywhere.
 */
export function showcaseRecommendation(standing: ShowcaseStanding | null): MyPokemonRecommendation | null {
  if (!standing || !standing.pick) return null;
  const parts = standing.types
    .filter((t) => t.rank <= SHOWCASE_PICKS_PER_DAY)
    .map((t) => `#${t.rank} ${typeLabel(t.type)}`);
  if (standing.pickOverall) parts.push(`#${standing.overall.rank} overall`);
  return {
    kind: "showcase",
    headline: `Showcase pick: ${parts.join(", ")}. Keep it.`,
    detail: `A showcase day takes ${SHOWCASE_PICKS_PER_DAY} entries, and this is one of your ${SHOWCASE_PICKS_PER_DAY} biggest ${
      standing.pickTypes.length > 0 ? typeLabel(standing.pickTypes[0]) + " Pokémon" : "Pokémon"
    }.`,
  };
}
